import React, { useEffect, useRef, useState, useCallback } from 'react'
import {
	Dialog,
	DialogContent,
	Typography,
	Box,
	Stack,
	CircularProgress,
	Alert,
	Checkbox,
	FormControlLabel,
	IconButton,
	useMediaQuery,
	useTheme,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { supabase } from '../utils/supabase'
import { getCurrencySymbol } from '../utils/currencyUtils'
import { L } from './ui/LavDropdown'
import { MbIc } from './appointment-details/mbBits'

const isDev = process.env.REACT_APP_ENV === 'development'

const SQUARE_SDK_URL = isDev
	? 'https://sandbox.web.squarecdn.com/v1/square.js'
	: 'https://web.squarecdn.com/v1/square.js'

const SQUARE_APP_ID = isDev
	? (process.env.REACT_APP_SQUARE_APP_ID_DEV || '')
	: (process.env.REACT_APP_SQUARE_APP_ID || '')

// Single-flight script loader so multiple dialog opens reuse the same <script> tag.
let squareSdkPromise = null
function loadSquareSdk() {
	if (typeof window === 'undefined') return Promise.reject(new Error('No window'))
	if (window.Square) return Promise.resolve(window.Square)
	if (squareSdkPromise) return squareSdkPromise

	squareSdkPromise = new Promise((resolve, reject) => {
		const existing = document.querySelector(`script[src="${SQUARE_SDK_URL}"]`)
		if (existing) {
			existing.addEventListener('load', () => resolve(window.Square))
			existing.addEventListener('error', () => reject(new Error('Failed to load Square SDK')))
			return
		}
		const script = document.createElement('script')
		script.src = SQUARE_SDK_URL
		script.async = true
		script.onload = () => resolve(window.Square)
		script.onerror = () => {
			squareSdkPromise = null
			reject(new Error('Failed to load Square SDK'))
		}
		document.head.appendChild(script)
	})
	return squareSdkPromise
}

/**
 * Mobile-friendly dialog that collects a credit card via Square's Web Payments SDK,
 * tokenizes it, and charges the merchant's connected Square account.
 *
 * Supports three modes via a single checkbox whose label depends on whether
 * the client already has a saved card:
 *   1. Client has saved card + checkbox CHECKED → charge the saved card.
 *   2. Client has saved card + checkbox UNCHECKED → one-time charge with a new card.
 *   3. Client has NO saved card + checkbox CHECKED → charge new card AND save it.
 *   4. Client has NO saved card + checkbox UNCHECKED → one-time charge with a new card.
 *
 * Props:
 *   open               - whether the dialog is visible
 *   onClose            - called when user cancels or closes
 *   merchant           - merchant row (needs square_location_id, currency)
 *   client             - client row (id, name, last_name, email, square_card_*)
 *   amountCents        - amount to charge in cents
 *   idempotencyKey     - unique key for the charge (e.g. appointmentId-timestamp)
 *   note               - optional note to attach on the Square payment
 *   onChargeSuccess    - called with { paymentId } after a successful charge
 */
export default function SquareCardDialog({
	open,
	onClose,
	merchant,
	client,
	amountCents,
	idempotencyKey,
	note,
	onChargeSuccess,
}) {
	const theme = useTheme()
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

	const containerIdRef = useRef(`square-card-container-${Math.random().toString(36).slice(2, 10)}`)
	const cardRef = useRef(null)
	const paymentsRef = useRef(null)

	const hasSavedCard = Boolean(client?.square_card_id)
	// Two independent checkboxes:
	//   1. useSavedCardChecked — only meaningful when hasSavedCard. Default ON.
	//      Unchecking switches to the card form.
	//   2. saveCardChecked — only meaningful when the card form is shown. Default OFF.
	//      Checked → tokenize + verify + save card-on-file (will replace any existing
	//      saved card for this client).
	const [useSavedCardChecked, setUseSavedCardChecked] = useState(hasSavedCard)
	const [saveCardChecked, setSaveCardChecked] = useState(false)
	const [sdkLoading, setSdkLoading] = useState(false)
	const [sdkError, setSdkError] = useState('')
	const [submitting, setSubmitting] = useState(false)
	const [submitError, setSubmitError] = useState('')

	const locationId = merchant?.square_location_id || ''
	const currencyCode = merchant?.currency || 'USD'

	const useSavedCard = hasSavedCard && useSavedCardChecked
	const needsCardForm = !useSavedCard
	const saveCard = needsCardForm && saveCardChecked

	const teardownCard = useCallback(async () => {
		try {
			if (cardRef.current) await cardRef.current.destroy()
		} catch {
			// best-effort cleanup
		}
		cardRef.current = null
		paymentsRef.current = null
	}, [])

	// Reset checkboxes to their defaults whenever the dialog opens.
	useEffect(() => {
		if (open) {
			setUseSavedCardChecked(hasSavedCard)
			setSaveCardChecked(false)
		}
	}, [open, hasSavedCard])

	// Initialize Square Web Payments SDK + mount the card form whenever we need one.
	useEffect(() => {
		if (!open || !needsCardForm) return

		let cancelled = false

		const init = async () => {
			setSdkError('')
			setSubmitError('')

			if (!SQUARE_APP_ID) {
				setSdkError('Square is not configured. Please contact support.')
				return
			}
			if (!locationId) {
				setSdkError('Square location is missing. Reconnect your Square account.')
				return
			}

			setSdkLoading(true)
			try {
				const Square = await loadSquareSdk()
				if (cancelled) return

				const payments = Square.payments(SQUARE_APP_ID, locationId)
				const card = await payments.card({
					style: {
						input: {
							// Must be >= 16px to stop iOS Safari from zooming on focus.
							fontSize: '16px',
							color: '#2D3748',
							fontWeight: '500',
						},
						'input::placeholder': { color: '#A0AEC0' },
						'input.is-focus': { color: '#2D3748' },
						'input.is-error': { color: '#E53E3E' },
					},
				})
				if (cancelled) {
					try { await card.destroy() } catch { /* ignore */ }
					return
				}
				const selector = `#${containerIdRef.current}`
				for (let i = 0; i < 20 && !document.querySelector(selector); i++) {
					await new Promise(r => setTimeout(r, 50))
				}
				if (!document.querySelector(selector)) {
					try { await card.destroy() } catch { /* ignore */ }
					return
				}
				await card.attach(selector)
				if (cancelled) {
					try { await card.destroy() } catch { /* ignore */ }
					return
				}
				paymentsRef.current = payments
				cardRef.current = card
			} catch (err) {
				console.error('Square SDK init error:', err)
				if (!cancelled) setSdkError(err.message || 'Failed to initialize card form.')
			} finally {
				if (!cancelled) setSdkLoading(false)
			}
		}

		init()

		return () => {
			cancelled = true
			teardownCard()
		}
	}, [open, needsCardForm, locationId, teardownCard])

	const handlePay = async () => {
		setSubmitting(true)
		setSubmitError('')
		try {
			let sourceId
			let verificationToken

			if (useSavedCard) {
				// No tokenization needed — backend uses the stored card.id directly.
			} else {
				if (!cardRef.current) throw new Error('Card form not ready.')
				const tokenResult = await cardRef.current.tokenize()
				if (tokenResult.status !== 'OK') {
					const detail = tokenResult.errors?.map(e => e.message).join(', ')
					throw new Error(detail || 'Card validation failed.')
				}
				sourceId = tokenResult.token

				// Saving the card to a customer requires SCA verification via verifyBuyer.
				if (saveCard) {
					if (!paymentsRef.current) throw new Error('Payments SDK not ready.')
					const verification = await paymentsRef.current.verifyBuyer(sourceId, {
						amount: '0.00',
						currencyCode,
						intent: 'STORE',
						billingContact: {
							givenName: client?.name || 'Customer',
							...(client?.last_name ? { familyName: client.last_name } : {}),
							...(client?.email ? { email: client.email } : {}),
						},
					})
					verificationToken = verification?.token
					if (!verificationToken) throw new Error('Card verification failed.')
				}
			}

			const { data, error } = await supabase.functions.invoke('square-card-charge', {
				body: {
					merchantId: merchant?.id,
					clientId: client?.id || null,
					sourceId,
					verificationToken,
					saveCard,
					useSavedCard,
					amountCents,
					idempotencyKey,
					note,
				},
			})

			if (error || data?.error) {
				let msg = data?.error || error?.message || 'Charge failed.'
				if (data?.detail) msg += ` (${data.detail})`
				throw new Error(msg)
			}

			onChargeSuccess?.({ paymentId: data?.paymentId })
		} catch (err) {
			console.error('Square card charge error:', err)
			setSubmitError(err.message || 'Charge failed. Please try again.')
		} finally {
			setSubmitting(false)
		}
	}

	const cs = getCurrencySymbol(merchant?.currency)
	const formattedAmount = ((amountCents || 0) / 100).toFixed(2)
	const savedLast4 = client?.square_card_last4 || '••••'
	const savedBrand = client?.square_card_brand || 'Card'

	const saveCardLabel = hasSavedCard
		? `Save this card and replace the saved ${savedBrand} ending in ${savedLast4}`
		: 'Save this card to the client for future payments'

	const payDisabled = submitting || (needsCardForm && (sdkLoading || Boolean(sdkError) || !cardRef.current))

	// Lavender checkbox styling shared by both toggles.
	const checkboxSx = {
		color: L.border,
		'&.Mui-checked': { color: L.primary },
	}
	const checkLabelSx = {
		m: 0,
		alignItems: 'flex-start',
		'& .MuiFormControlLabel-label': {
			fontFamily: L.font, fontSize: '13.5px', fontWeight: 600, color: L.text, pt: '9px',
		},
	}

	return (
		<Dialog
			open={open}
			onClose={submitting ? undefined : onClose}
			maxWidth="xs"
			fullWidth
			fullScreen={isMobile}
			disableEnforceFocus
			disableAutoFocus
			disableRestoreFocus
			PaperProps={{
				sx: {
					bgcolor: L.bg,
					color: L.text,
					fontFamily: L.font,
					borderRadius: isMobile ? 0 : '20px',
					overflow: 'hidden',
				},
			}}
		>
			{/* Header */}
			<Box sx={{
				px: '22px',
				pt: isMobile ? 'calc(env(safe-area-inset-top, 0px) + 20px)' : '20px',
				pb: '14px',
				display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px',
				borderBottom: `1px solid ${L.border}`,
			}}>
				<Box sx={{ display: 'flex', alignItems: 'center', gap: '11px' }}>
					<Box sx={{
						width: 34, height: 34, flex: 'none', borderRadius: '11px',
						bgcolor: L.primarySoft, color: L.primaryDeep,
						display: 'flex', alignItems: 'center', justifyContent: 'center',
					}}>
						<MbIc name="card2" size={17} />
					</Box>
					<Box>
						<Typography sx={{ fontSize: '19px', fontWeight: 700, letterSpacing: '-0.01em', color: L.text, fontFamily: L.font, m: 0 }}>
							Pay with Card
						</Typography>
						<Typography sx={{ fontSize: '12.5px', color: L.muted, mt: '2px', fontFamily: L.font }}>
							Square secure payment · {cs}{formattedAmount}
						</Typography>
					</Box>
				</Box>
				<IconButton
					size="small"
					onClick={submitting ? undefined : onClose}
					disabled={submitting}
					sx={{
						width: 28, height: 28, borderRadius: '50%',
						border: `1px solid ${L.border}`, bgcolor: L.surface,
						color: L.muted, flexShrink: 0,
						'&:hover': { color: L.text, borderColor: L.muted },
					}}
				>
					<CloseIcon sx={{ fontSize: 15 }} />
				</IconButton>
			</Box>

			<DialogContent sx={{ p: 0, '&.MuiDialogContent-root': { overflowY: 'auto' } }}>
				<Stack sx={{ px: '22px', pt: '16px', pb: '18px', gap: '14px' }}>
					<Typography sx={{ fontSize: '13.5px', fontWeight: 600, color: L.muted, fontFamily: L.font, lineHeight: 1.5 }}>
						{useSavedCard
							? <>The client's saved card will be charged <Box component="b" sx={{ color: L.text }}>{cs}{formattedAmount}</Box>.</>
							: <>Enter the customer's card details below. The card will be charged <Box component="b" sx={{ color: L.text }}>{cs}{formattedAmount}</Box>.</>
						}
					</Typography>

					{sdkError && (
						<Alert severity="error" sx={{
							borderRadius: L.rSm, fontFamily: L.font, fontSize: '13px',
							bgcolor: '#FFF0F0', color: '#8B1A1A', border: '1px solid #FECACA',
							'& .MuiAlert-icon': { color: '#EF4444' },
						}}>
							{sdkError}
						</Alert>
					)}
					{submitError && (
						<Alert severity="error" sx={{
							borderRadius: L.rSm, fontFamily: L.font, fontSize: '13px',
							bgcolor: '#FFF0F0', color: '#8B1A1A', border: '1px solid #FECACA',
							'& .MuiAlert-icon': { color: '#EF4444' },
						}}>
							{submitError}
						</Alert>
					)}

					{/* "Use saved card" — only when this client already has a saved card. */}
					{client?.id && hasSavedCard && (
						<Box sx={{
							border: `1.5px solid ${useSavedCardChecked ? L.primary : L.border}`,
							bgcolor: useSavedCardChecked ? L.primarySoft : L.field,
							borderRadius: '13px', px: '10px', py: '4px',
						}}>
							<FormControlLabel
								control={
									<Checkbox
										checked={useSavedCardChecked}
										onChange={(e) => setUseSavedCardChecked(e.target.checked)}
										disabled={submitting}
										sx={checkboxSx}
									/>
								}
								label={`Use saved card (${savedBrand} ending in ${savedLast4})`}
								sx={checkLabelSx}
							/>
						</Box>
					)}

					{/* Card form — hidden when charging the saved card */}
					{needsCardForm && (
						<>
							{/* "Save this card" — only when we have a client to attach it to. */}
							{client?.id && (
								<Box sx={{
									border: `1.5px solid ${saveCardChecked ? L.primary : L.border}`,
									bgcolor: saveCardChecked ? L.primarySoft : L.field,
									borderRadius: '13px', px: '10px', py: '4px',
								}}>
									<FormControlLabel
										control={
											<Checkbox
												checked={saveCardChecked}
												onChange={(e) => setSaveCardChecked(e.target.checked)}
												disabled={submitting}
												sx={checkboxSx}
											/>
										}
										label={saveCardLabel}
										sx={checkLabelSx}
									/>
								</Box>
							)}
							<Box sx={{
								position: 'relative', minHeight: 140,
								border: `1.5px solid ${L.border}`, borderRadius: '13px',
								bgcolor: L.field, p: '14px 14px 4px',
							}}>
								{sdkLoading && (
									<Box sx={{
										position: 'absolute', inset: 0,
										display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1,
									}}>
										<CircularProgress size={18} sx={{ color: L.primary }} />
										<Typography sx={{ fontSize: '13px', fontWeight: 600, color: L.muted, fontFamily: L.font }}>
											Loading secure card form…
										</Typography>
									</Box>
								)}
								<Box
									id={containerIdRef.current}
									sx={{
										width: '100%',
										minHeight: 140,
										opacity: sdkLoading ? 0 : 1,
									}}
								/>
							</Box>
						</>
					)}

					<Typography sx={{ fontSize: '11.5px', fontWeight: 600, color: L.muted, fontFamily: L.font }}>
						🔒 Card details are tokenized securely by Square. We never see or store the full card number.
					</Typography>
				</Stack>
			</DialogContent>

			{/* Footer */}
			<Box sx={{
				px: '22px', pt: '14px',
				pb: isMobile ? 'calc(env(safe-area-inset-bottom, 0px) + 20px)' : '18px',
				borderTop: `1px solid ${L.border}`,
				display: 'flex', gap: '10px', justifyContent: 'flex-end',
				bgcolor: L.bg,
			}}>
				<Box
					component="button"
					type="button"
					onClick={onClose}
					disabled={submitting}
					sx={{
						px: isMobile ? '22px' : '16px', height: isMobile ? 52 : 42,
						borderRadius: isMobile ? '14px' : L.rSm, flex: 'none',
						border: `1.5px solid ${L.border}`, bgcolor: isMobile ? '#FFFFFF' : 'transparent',
						fontSize: isMobile ? '15px' : '14px', fontWeight: 650, color: L.muted,
						fontFamily: L.font, cursor: 'pointer',
						'&:hover:not(:disabled)': { color: L.text, borderColor: L.muted },
						'&:disabled': { opacity: 0.5, cursor: 'default' },
					}}
				>
					Cancel
				</Box>
				<Box
					component="button"
					type="button"
					onClick={handlePay}
					disabled={payDisabled}
					sx={{
						display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
						height: isMobile ? 52 : 42, px: '18px',
						borderRadius: isMobile ? '14px' : L.rSm, border: 'none',
						flex: isMobile ? 1 : 'none',
						bgcolor: L.primary, color: '#FFFFFF',
						fontSize: isMobile ? '15.5px' : '14px', fontWeight: isMobile ? 750 : 700,
						fontFamily: L.font, whiteSpace: 'nowrap', cursor: 'pointer',
						boxShadow: `0 2px 8px ${L.primarySoft}`,
						'&:hover:not(:disabled)': { bgcolor: L.primaryDeep },
						'&:disabled': { opacity: 0.5, cursor: 'default', boxShadow: 'none' },
					}}
				>
					{submitting ? <CircularProgress size={16} sx={{ color: '#FFFFFF' }} /> : <MbIc name="card2" size={16} />}
					<span>{submitting ? 'Charging…' : `Charge ${cs}${formattedAmount}`}</span>
				</Box>
			</Box>
		</Dialog>
	)
}
